/**
 * Esquemas de personas (usuarios del tablero) y de sus tokens de API.
 */
import { z } from 'zod';
import { ROLES } from './dominio';
import { fechaIsoSchema } from './comunes';

/** Lo mínimo de una persona para mostrarla (por ejemplo, como responsable de una tarea). */
export const usuarioResumenSchema = z.object({
  id: z.number().int(),
  nombre: z.string(),
  email: z.string(),
});
export type UsuarioResumen = z.infer<typeof usuarioResumenSchema>;

/** Persona completa, tal como la ve un administrador. */
export const usuarioSchema = usuarioResumenSchema.extend({
  rol: z.enum(ROLES),
  activo: z.boolean(),
  ultimoAcceso: fechaIsoSchema.nullable(),
});
export type Usuario = z.infer<typeof usuarioSchema>;

/** Quién soy: la persona con sesión, más lo que la API le permite hacer. */
export const yoSchema = usuarioSchema.extend({
  puedeEscribir: z.boolean(),
  puedeAdministrar: z.boolean(),
});
export type Yo = z.infer<typeof yoSchema>;

/** Cambios que un administrador puede hacer sobre una persona. */
export const actualizarUsuarioSchema = z
  .object({
    rol: z.enum(ROLES).optional(),
    activo: z.boolean().optional(),
  })
  .refine((datos) => datos.rol !== undefined || datos.activo !== undefined, {
    message: 'Indica al menos un cambio (rol o activo).',
  });
export type ActualizarUsuarioEntrada = z.infer<typeof actualizarUsuarioSchema>;

/** Inicio de sesión de desarrollo (solo existe fuera de producción). */
export const loginDesarrolloSchema = z.object({
  email: z.email().max(200),
  nombre: z.string().trim().min(1).max(100).optional(),
});
export type LoginDesarrolloEntrada = z.infer<typeof loginDesarrolloSchema>;

/** Datos públicos de configuración que la web necesita antes de iniciar sesión. */
export const configuracionPublicaSchema = z.object({
  loginDesarrolloHabilitado: z.boolean(),
  dominioPermitido: z.string(),
});
export type ConfiguracionPublica = z.infer<typeof configuracionPublicaSchema>;

/**
 * Vencimientos que se pueden elegir al crear un token: 30 o 90 días, o «nunca».
 * Se envían como texto (no como número de días libre) para que solo existan estas opciones.
 */
export const VENCIMIENTOS_TOKEN = ['30', '90', 'nunca'] as const;
export type VencimientoToken = (typeof VENCIMIENTOS_TOKEN)[number];

/**
 * 90 días por defecto: un token que se filtra deja de servir solo, sin depender de que alguien se
 * acuerde de revocarlo, y 90 días alcanza para un script sin obligar a renovarlo cada mes.
 * «Sin vencimiento» queda como elección explícita.
 */
export const VENCIMIENTO_TOKEN_POR_DEFECTO: VencimientoToken = '90';

export const ETIQUETA_VENCIMIENTO_TOKEN: Record<VencimientoToken, string> = {
  '30': '30 días',
  '90': '90 días',
  nunca: 'Sin vencimiento',
};

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** Instante en que vence un token creado en `desde`, o `null` si no vence. */
export function calcularExpiracionToken(vencimiento: VencimientoToken, desde: Date): Date | null {
  if (vencimiento === 'nunca') return null;
  return new Date(desde.getTime() + Number(vencimiento) * MS_POR_DIA);
}

/**
 * Un token está vencido desde el instante exacto de `expiraEn` (inclusive): sirve mientras
 * `ahora < expiraEn`. Sin fecha de vencimiento, nunca vence.
 */
export function tokenVencido(expiraEn: Date | string | null, ahora: Date): boolean {
  if (expiraEn === null) return false;
  return ahora.getTime() >= new Date(expiraEn).getTime();
}

/** Pedido para crear un token de API (para scripts o para Claude). */
export const crearTokenSchema = z.object({
  nombre: z.string().trim().min(1).max(80),
  vencimiento: z.enum(VENCIMIENTOS_TOKEN).default(VENCIMIENTO_TOKEN_POR_DEFECTO),
});
export type CrearTokenEntrada = z.infer<typeof crearTokenSchema>;

/** Token tal como se lista: nunca incluye el valor secreto, solo un prefijo para reconocerlo. */
export const tokenSchema = z.object({
  id: z.number().int(),
  nombre: z.string(),
  prefijo: z.string(),
  creadoEn: fechaIsoSchema,
  ultimoUso: fechaIsoSchema.nullable(),
  revocadoEn: fechaIsoSchema.nullable(),
  /** Cuándo deja de servir; `null` si no vence (también los tokens creados antes de existir el vencimiento). */
  expiraEn: fechaIsoSchema.nullable(),
  /** Calculado por la API con su reloj, para que la web no dependa de la hora del computador. */
  vencido: z.boolean(),
});
export type TokenApi = z.infer<typeof tokenSchema>;

/** Respuesta al crear un token: el valor completo se entrega UNA sola vez. */
export const tokenCreadoSchema = tokenSchema.extend({
  token: z.string(),
});
export type TokenCreado = z.infer<typeof tokenCreadoSchema>;
